import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { AlertModal } from "../components/shared/AlertModal";
import { ConfirmModal } from "../components/shared/ConfirmModal";
import { DetailPageSkeleton } from "../components/ui/Skeleton";
import { aiApi, getAiApiErrorMessage, type CreditReportParseResultDto } from "../api/aiApi";
import { CreditReportParseModal } from "../components/client/CreditReportParseModal";
import { clientsApi } from "../api/clientsApi";
import { CreateDisputeRequest, disputesApi, type UpdateDisputeRequest } from "../api/disputesApi";
import { disputeLettersApi } from "../api/disputeLettersApi";
import {
  documentsApi,
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
  validateDocumentFile,
  ClientDocument,
  type DocumentCategory,
} from "../api/documentsApi";
import {
  contractsApi,
  ContractSignatureStatus,
  ContractSignatureStatuses,
  ContractSignatureSummaryResponse,
  ContractTemplateResponse,
} from "../api/contractsApi";
import { settingsApi } from "../api/settingsApi";
import { getApiErrorPayload } from "../utils/apiError";
import { Client, CreditAnalysis, DisputeItem, DisputeLetter, LetterTemplate } from "../types";
import ClientBillingTab from "./ClientBillingTab";

const DISPUTE_ACCOUNT_TYPES = ["Collection", "Late Payment", "Charge-off", "Inquiry", "Other"] as const;
const DISPUTE_STATUSES = ["In Dispute", "Awaiting Response", "Updated", "Deleted/Resolved"] as const;

const LETTER_BUREAU_TARGETS = ["Equifax", "Experian", "TransUnion", "Creditor / Furnisher"] as const;

function isoToDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso?.trim()) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalValueToIso(local: string): string | null {
  if (!local.trim()) {
    return null;
  }
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const FALLBACK_LETTER_TEMPLATES: LetterTemplate[] = [
  { typeKey: "Validation", name: "Validation", description: "", guidance: "" },
  { typeKey: "Investigation", name: "Investigation", description: "", guidance: "" },
  { typeKey: "Goodwill", name: "Goodwill", description: "", guidance: "" },
  { typeKey: "Other", name: "Other", description: "", guidance: "" },
];

const modalInputClass =
  "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-blue-500/40 focus:ring-2";
const modalCardClass =
  "rounded-xl border border-slate-800 bg-slate-900/95 p-4 shadow-lg shadow-black/20 sm:p-6";

function coerceActionStepItem(item: unknown): string | null {
  if (item === null || item === undefined) {
    return null;
  }
  if (typeof item === "string") {
    const t = item.trim();
    return t || null;
  }
  if (typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }
  if (typeof item === "object") {
    const o = item as Record<string, unknown>;
    const step = o.step ?? o.text ?? o.title;
    const desc = o.description ?? o.detail ?? o.body;
    if (typeof step === "string" && typeof desc === "string") {
      const a = step.trim();
      const b = desc.trim();
      if (a && b) {
        return `${a}: ${b}`;
      }
      return a || b || null;
    }
    if (typeof step === "string" && step.trim()) {
      return step.trim();
    }
    if (typeof desc === "string" && desc.trim()) {
      return desc.trim();
    }
  }
  return null;
}

function parseActionPlanStepsJson(raw: string | undefined): string[] {
  if (!raw?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map(coerceActionStepItem).filter((s): s is string => Boolean(s));
    }
  } catch {
    // fall through
  }

  return raw
    .replace("[", "")
    .replace("]", "")
    .split(",")
    .map((s) => s.trim().replace(/^"+|"+$/g, ""))
    .filter(Boolean);
}

function parseActionStepsFromFullJson(fullJson: string | undefined): string[] {
  if (!fullJson?.trim()) {
    return [];
  }
  try {
    const o = JSON.parse(fullJson) as Record<string, unknown>;
    let stepsRaw: unknown;
    const apfc = o.action_plan_for_client;
    if (apfc && typeof apfc === "object" && !Array.isArray(apfc)) {
      const inner = (apfc as Record<string, unknown>).steps;
      if (Array.isArray(inner)) {
        stepsRaw = inner;
      }
    }
    if (stepsRaw === undefined && Array.isArray(o.actionSteps)) {
      stepsRaw = o.actionSteps;
    }
    if (Array.isArray(stepsRaw)) {
      return stepsRaw.map(coerceActionStepItem).filter((s): s is string => Boolean(s));
    }
  } catch {
    /* ignore */
  }
  return [];
}

function parseHeadlineFromFullJson(fullJson: string | undefined, fallback: string): string {
  const base = fallback?.trim();
  if (base) {
    return base;
  }
  if (!fullJson?.trim()) {
    return "";
  }
  try {
    const o = JSON.parse(fullJson) as Record<string, unknown>;
    const h = o.actionPlanHeadline ?? o.ActionPlanHeadline;
    if (typeof h === "string" && h.trim()) {
      return h.trim();
    }
  } catch {
    /* ignore */
  }
  return "";
}

function parseScoreField(v: unknown): number | null {
  if (typeof v === "number" && !Number.isNaN(v)) {
    return v;
  }
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function parseScoreRangeFromFullJson(
  fullJson: string | undefined,
  apiLow: number,
  apiHigh: number
): { low: number; high: number } {
  if (fullJson?.trim()) {
    try {
      const o = JSON.parse(fullJson) as Record<string, unknown>;
      const resolved = o.estimatedScoreRangeIfResolved;
      if (resolved && typeof resolved === "object" && !Array.isArray(resolved)) {
        const ro = resolved as Record<string, unknown>;
        const l = parseScoreField(ro.low);
        const h = parseScoreField(ro.high);
        if (l !== null && h !== null) {
          return { low: l, high: h };
        }
      }
      const l =
        typeof o.estimatedScoreImprovementLow === "number"
          ? o.estimatedScoreImprovementLow
          : typeof o.EstimatedScoreImprovementLow === "number"
            ? o.EstimatedScoreImprovementLow
            : null;
      const h =
        typeof o.estimatedScoreImprovementHigh === "number"
          ? o.estimatedScoreImprovementHigh
          : typeof o.EstimatedScoreImprovementHigh === "number"
            ? o.EstimatedScoreImprovementHigh
            : null;
      if (l !== null && h !== null) {
        return { low: l, high: h };
      }
    } catch {
      /* ignore */
    }
  }
  if ((apiLow !== 0 || apiHigh !== 0) && !Number.isNaN(apiLow) && !Number.isNaN(apiHigh)) {
    return { low: apiLow, high: apiHigh };
  }
  return { low: 0, high: 0 };
}

function isPdfDocument(doc: { fileName: string; contentType: string }): boolean {
  const ext = doc.fileName.split(".").pop()?.toLowerCase() ?? "";
  return ext === "pdf" || doc.contentType.toLowerCase().includes("pdf");
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function documentCategoryLabel(cat: string): string {
  return DOCUMENT_CATEGORY_LABELS[cat as DocumentCategory] ?? cat;
}

function FileTypeIcon({ fileName, fileType }: { fileName: string; fileType: string }) {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  const isPdf = ext === "pdf" || fileType.toLowerCase().includes("pdf");
  const isImage =
    fileType.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext);

  if (isPdf) {
    return (
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-rose-500/30 bg-rose-500/10 text-[10px] font-bold tracking-wide text-rose-300">
        PDF
      </span>
    );
  }
  if (isImage) {
    return (
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-sky-500/30 bg-sky-500/10 text-[10px] font-bold tracking-wide text-sky-300">
        IMG
      </span>
    );
  }
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-violet-500/30 bg-violet-500/10 text-[10px] font-bold tracking-wide text-violet-200">
      DOC
    </span>
  );
}

type TabKey = "overview" | "disputes" | "documents" | "ai" | "billing" | "notes";

const tabs: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "disputes", label: "Disputes" },
  { key: "documents", label: "Documents" },
  { key: "ai", label: "AI Analysis" },
  { key: "billing", label: "Billing" },
  { key: "notes", label: "Notes" },
];

function statusPillClasses(status: ContractSignatureStatus): string {
  switch (status) {
    case ContractSignatureStatuses.Sent:
      return "bg-blue-500/15 text-blue-200 border-blue-500/40";
    case ContractSignatureStatuses.Opened:
      return "bg-indigo-500/15 text-indigo-200 border-indigo-500/40";
    case ContractSignatureStatuses.Signed:
      return "bg-emerald-500/15 text-emerald-200 border-emerald-500/40";
    case ContractSignatureStatuses.Voided:
      return "bg-slate-500/20 text-slate-200 border-slate-500/40";
    case ContractSignatureStatuses.Expired:
      return "bg-amber-500/15 text-amber-200 border-amber-500/40";
    default:
      return "bg-slate-500/20 text-slate-300 border-slate-500/40";
  }
}

function statusLabel(status: ContractSignatureStatus): string {
  switch (status) {
    case ContractSignatureStatuses.Sent:
      return "Sent";
    case ContractSignatureStatuses.Opened:
      return "Opened";
    case ContractSignatureStatuses.Signed:
      return "Signed";
    case ContractSignatureStatuses.Voided:
      return "Voided";
    case ContractSignatureStatuses.Expired:
      return "Expired";
    default:
      return "Unknown";
  }
}

interface ServiceAgreementsSectionProps {
  loading: boolean;
  contracts: ContractSignatureSummaryResponse[];
  error: string;
  actionError: { message: string; isSignerConfig: boolean } | null;
  onDismissActionError: () => void;
  signerConfigKnown: boolean;
  signerConfigured: boolean;
  sendDisabledReason: string | null;
  onSend: () => void;
  onVoid: (row: ContractSignatureSummaryResponse) => void;
  onResend: (row: ContractSignatureSummaryResponse) => void;
  onDownload: (row: ContractSignatureSummaryResponse) => void;
}

const ServiceAgreementsSection: React.FC<ServiceAgreementsSectionProps> = ({
  loading,
  contracts,
  error,
  actionError,
  onDismissActionError,
  signerConfigKnown,
  signerConfigured,
  sendDisabledReason,
  onSend,
  onVoid,
  onResend,
  onDownload,
}) => {
  const showSignerBanner = signerConfigKnown && !signerConfigured;
  const sendDisabled = sendDisabledReason !== null;
  return (
    <section className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Service Agreements</h3>
          <p className="mt-1 text-sm text-slate-400">
            CROA-compliant e-signature contracts. The signed PDF is filed automatically when the
            client completes signing.
          </p>
        </div>
        <button
          type="button"
          onClick={onSend}
          disabled={sendDisabled}
          title={sendDisabledReason ?? undefined}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send for signature
        </button>
      </div>

      {showSignerBanner && (
        <div className="rounded-lg border border-amber-500/45 bg-amber-950/30 px-3 py-2 text-sm text-amber-100">
          <p className="font-semibold text-amber-200">Operator signer not set</p>
          <p className="mt-1">
            Set your operator signer name and title in{" "}
            <Link to="/settings" className="font-medium text-amber-200 underline hover:text-amber-100">
              Settings
            </Link>{" "}
            before sending contracts.
          </p>
        </div>
      )}

      {actionError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-rose-500/40 bg-rose-950/30 px-3 py-2 text-sm text-rose-200">
          <div>
            <p>{actionError.message}</p>
            {actionError.isSignerConfig && (
              <p className="mt-1 text-xs">
                Open{" "}
                <Link to="/settings" className="underline hover:text-rose-100">
                  Settings
                </Link>{" "}
                to configure your operator signer.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onDismissActionError}
            className="text-xs text-rose-300 hover:text-rose-100"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {error && <p className="text-sm text-rose-300">{error}</p>}

      {loading ? (
        <p className="text-sm text-slate-400">Loading service agreements…</p>
      ) : contracts.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">No service agreements sent yet.</p>
      ) : (
        <ul className="space-y-2">
          {contracts.map((row) => {
            const isPending =
              row.status === ContractSignatureStatuses.Sent ||
              row.status === ContractSignatureStatuses.Opened;
            const isSigned = row.status === ContractSignatureStatuses.Signed;
            const canResend =
              row.status === ContractSignatureStatuses.Voided ||
              row.status === ContractSignatureStatuses.Expired;
            const sentDate = new Date(row.sentAt).toLocaleDateString();
            const signedDate = row.signedAt ? new Date(row.signedAt).toLocaleDateString() : null;
            return (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-white">{row.templateName}</span>
                    <span
                      className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${statusPillClasses(row.status)}`}
                    >
                      {statusLabel(row.status)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Sent {sentDate}
                    {signedDate ? ` · Signed ${signedDate}` : ""}
                    {row.integrityHashSha256Prefix
                      ? ` · SHA-256 ${row.integrityHashSha256Prefix}…`
                      : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {isSigned && (
                    <button
                      type="button"
                      onClick={() => onDownload(row)}
                      className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700"
                    >
                      Download PDF
                    </button>
                  )}
                  {isPending && (
                    <button
                      type="button"
                      onClick={() => onVoid(row)}
                      className="rounded-lg border border-rose-500/40 bg-rose-950/30 px-3 py-1.5 text-xs font-medium text-rose-200 hover:bg-rose-950/50"
                    >
                      Void
                    </button>
                  )}
                  {canResend && (
                    <button
                      type="button"
                      onClick={() => onResend(row)}
                      className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-950/50"
                    >
                      Resend
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [client, setClient] = useState<Client | null>(null);
  const [disputes, setDisputes] = useState<DisputeItem[]>([]);
  const [analysis, setAnalysis] = useState<CreditAnalysis | null>(null);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiError, setAiError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState("");
  const [docDeleteTarget, setDocDeleteTarget] = useState<{ id: string; fileName: string } | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("All");
  const [uploadCategory, setUploadCategory] = useState<string>("Other");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // --- Contract e-signature (Phase 7.3) ---
  const [contracts, setContracts] = useState<ContractSignatureSummaryResponse[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsError, setContractsError] = useState("");
  const [contractActionError, setContractActionError] = useState<{ message: string; isSignerConfig: boolean } | null>(null);
  const [signerLegalName, setSignerLegalName] = useState<string | null>(null);
  const [signerTitle, setSignerTitle] = useState<string | null>(null);
  const [contractTemplates, setContractTemplates] = useState<ContractTemplateResponse[]>([]);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendTemplateId, setSendTemplateId] = useState<string>("");
  const [sending, setSending] = useState(false);
  const [voidTarget, setVoidTarget] = useState<ContractSignatureSummaryResponse | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [resendTarget, setResendTarget] = useState<ContractSignatureSummaryResponse | null>(null);
  const [resending, setResending] = useState(false);

  const [parseModalOpen, setParseModalOpen] = useState(false);
  const [parseLoadingDocId, setParseLoadingDocId] = useState<string | null>(null);
  const [parseView, setParseView] = useState<CreditReportParseResultDto | null>(null);
  const [parseApplying, setParseApplying] = useState(false);
  const [parseModalError, setParseModalError] = useState("");
  const [applyReportSuccessOpen, setApplyReportSuccessOpen] = useState(false);
  const [applyReportSuccessMessage, setApplyReportSuccessMessage] = useState("");

  const [addDisputeOpen, setAddDisputeOpen] = useState(false);
  const [disputeSaving, setDisputeSaving] = useState(false);
  const [disputeFormError, setDisputeFormError] = useState("");
  const [letterTemplates, setLetterTemplates] = useState<LetterTemplate[]>([]);
  const [dCreditor, setDCreditor] = useState("");
  const [dAccountType, setDAccountType] = useState<string>(DISPUTE_ACCOUNT_TYPES[0]);
  const [dAccountNumber, setDAccountNumber] = useState("");
  const [dBalance, setDBalance] = useState("");
  const [dStatus, setDStatus] = useState<string>(DISPUTE_STATUSES[0]);
  const [dDisputeReason, setDDisputeReason] = useState("");
  const [generatingReason, setGeneratingReason] = useState(false);
  const [dEquifax, setDEquifax] = useState(false);
  const [dExperian, setDExperian] = useState(false);
  const [dTransUnion, setDTransUnion] = useState(false);
  const [dLetterType, setDLetterType] = useState("");
  const [dNotes, setDNotes] = useState("");

  const [detailDisputeId, setDetailDisputeId] = useState<string | null>(null);
  const [detailDraft, setDetailDraft] = useState<DisputeItem | null>(null);
  const [detailLetters, setDetailLetters] = useState<DisputeLetter[]>([]);
  const [detailLettersLoading, setDetailLettersLoading] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailSaveError, setDetailSaveError] = useState("");
  const [detailLetterError, setDetailLetterError] = useState("");
  const [generatingDetailLetter, setGeneratingDetailLetter] = useState(false);
  const [savingDetailLetter, setSavingDetailLetter] = useState(false);
  const [generatedLetterBody, setGeneratedLetterBody] = useState("");
  const [letterGenLetterType, setLetterGenLetterType] = useState("");
  const [letterGenBureau, setLetterGenBureau] = useState<string>(LETTER_BUREAU_TARGETS[0]);
  const [expandedHistoryLetterId, setExpandedHistoryLetterId] = useState<string | null>(null);

  const refreshDisputes = useCallback(async () => {
    if (!id) {
      return;
    }
    const list = await disputesApi.getAllForClient(id);
    setDisputes(list);
  }, [id]);

  useEffect(() => {
    if (!addDisputeOpen) {
      return;
    }
    setDisputeFormError("");
    void disputeLettersApi.getTemplates().then(setLetterTemplates).catch(() => setLetterTemplates([]));
  }, [addDisputeOpen]);

  const disputeLetterOptions = useMemo(
    () => (letterTemplates.length > 0 ? letterTemplates : FALLBACK_LETTER_TEMPLATES),
    [letterTemplates]
  );

  useEffect(() => {
    if (!addDisputeOpen) {
      return;
    }
    setDLetterType((prev) => prev || disputeLetterOptions[0]?.typeKey || "");
  }, [addDisputeOpen, disputeLetterOptions]);

  const openAddDisputeModal = () => {
    setDCreditor("");
    setDAccountType(DISPUTE_ACCOUNT_TYPES[0]);
    setDAccountNumber("");
    setDBalance("");
    setDStatus(DISPUTE_STATUSES[0]);
    setDDisputeReason("");
    setGeneratingReason(false);
    setDEquifax(false);
    setDExperian(false);
    setDTransUnion(false);
    setDLetterType("");
    setDNotes("");
    setDisputeFormError("");
    setAddDisputeOpen(true);
  };

  const submitNewDispute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) {
      return;
    }
    setDisputeSaving(true);
    setDisputeFormError("");
    const balanceNum = Number.parseFloat(dBalance);
    if (dBalance.trim() === "" || Number.isNaN(balanceNum)) {
      setDisputeFormError("Enter a valid balance.");
      setDisputeSaving(false);
      return;
    }
    const now = new Date().toISOString();
    const payload: CreateDisputeRequest = {
      creditor: dCreditor.trim(),
      accountType: dAccountType,
      accountNumber: dAccountNumber.trim(),
      balance: balanceNum,
      status: dStatus,
      disputeReason: dDisputeReason.trim(),
      disputeStrategy: "",
      letterType: dLetterType.trim(),
      priorityRank: 0,
      estimatedScoreImpact: "",
      equifaxDisputed: dEquifax,
      experianDisputed: dExperian,
      transUnionDisputed: dTransUnion,
      roundNumber: 1,
      statuteOfLimitationsFlag: false,
      notes: dNotes.trim(),
      lastLetterSentAt: null,
      responseReceivedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    try {
      await disputesApi.create(id, payload);
      await refreshDisputes();
      setAddDisputeOpen(false);
    } catch {
      setDisputeFormError("Could not create dispute.");
    } finally {
      setDisputeSaving(false);
    }
  };

  const generateDisputeReasonWithAi = async () => {
    const creditor = dCreditor.trim();
    if (!creditor || !dAccountType) {
      return;
    }
    setGeneratingReason(true);
    setDisputeFormError("");
    try {
      const balanceParsed = Number.parseFloat(dBalance);
      const bureausParts: string[] = [];
      if (dEquifax) {
        bureausParts.push("Equifax");
      }
      if (dExperian) {
        bureausParts.push("Experian");
      }
      if (dTransUnion) {
        bureausParts.push("TransUnion");
      }
      const bureaus = bureausParts.length > 0 ? bureausParts.join(", ") : null;

      const result = await aiApi.generateDisputeReason({
        creditorName: creditor,
        accountType: dAccountType,
        accountNumber: dAccountNumber.trim() || undefined,
        balance: dBalance.trim() === "" || Number.isNaN(balanceParsed) ? null : balanceParsed,
        status: dStatus,
        bureaus,
        additionalContext: dNotes.trim() || null,
      });
      setDDisputeReason(result.reason);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not generate dispute reason.";
      setDisputeFormError(message);
    } finally {
      setGeneratingReason(false);
    }
  };

  const closeDetailDisputeModal = useCallback(() => {
    setDetailDisputeId(null);
    setDetailDraft(null);
    setDetailSaveError("");
    setDetailLetterError("");
    setGeneratedLetterBody("");
    setExpandedHistoryLetterId(null);
    setDetailLetters([]);
  }, []);

  const openDisputeDetail = useCallback(
    (d: DisputeItem) => {
      const options = disputeLetterOptions;
      const typeKey =
        options.find((t) => t.typeKey === d.letterType || t.name === d.letterType)?.typeKey ??
        (d.letterType || options[0]?.typeKey || "");
      setDetailDisputeId(d.id);
      setDetailDraft({ ...d, letterType: typeKey });
      setDetailSaveError("");
      setDetailLetterError("");
      setGeneratedLetterBody("");
      setLetterGenLetterType(typeKey || options[0]?.typeKey || "");
      setLetterGenBureau(LETTER_BUREAU_TARGETS[0]);
      setExpandedHistoryLetterId(null);
    },
    [disputeLetterOptions]
  );

  useEffect(() => {
    if (!detailDisputeId) {
      return;
    }
    void disputeLettersApi.getTemplates().then(setLetterTemplates).catch(() => setLetterTemplates([]));
  }, [detailDisputeId]);

  useEffect(() => {
    if (!detailDisputeId) {
      setDetailLetters([]);
      setDetailLettersLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLettersLoading(true);
    void disputeLettersApi
      .getLettersForDispute(detailDisputeId)
      .then((list) => {
        if (!cancelled) {
          setDetailLetters(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetailLetters([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLettersLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [detailDisputeId]);

  useEffect(() => {
    if (!detailDisputeId) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeDetailDisputeModal();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailDisputeId, closeDetailDisputeModal]);

  const submitDetailDisputeSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !detailDraft) {
      return;
    }
    const balanceNum = Number(detailDraft.balance);
    if (Number.isNaN(balanceNum)) {
      setDetailSaveError("Enter a valid balance.");
      return;
    }
    setDetailSaving(true);
    setDetailSaveError("");
    try {
      const d = detailDraft;
      const now = new Date().toISOString();
      const payload: UpdateDisputeRequest = {
        creditor: d.creditor.trim(),
        accountType: d.accountType,
        accountNumber: d.accountNumber.trim(),
        balance: balanceNum,
        status: d.status,
        disputeReason: d.disputeReason.trim(),
        disputeStrategy: d.disputeStrategy,
        letterType: d.letterType.trim(),
        priorityRank: d.priorityRank,
        estimatedScoreImpact: d.estimatedScoreImpact,
        equifaxDisputed: d.equifaxDisputed,
        experianDisputed: d.experianDisputed,
        transUnionDisputed: d.transUnionDisputed,
        roundNumber: d.roundNumber,
        statuteOfLimitationsFlag: d.statuteOfLimitationsFlag,
        notes: d.notes.trim(),
        lastLetterSentAt: d.lastLetterSentAt ?? null,
        responseReceivedAt: d.responseReceivedAt ?? null,
        updatedAt: now,
      };
      const updated = await disputesApi.update(id, d.id, payload);
      setDetailDraft(updated);
      await refreshDisputes();
    } catch {
      setDetailSaveError("Could not save dispute.");
    } finally {
      setDetailSaving(false);
    }
  };

  const handleGenerateDetailLetter = async () => {
    if (!detailDisputeId || !letterGenLetterType) {
      setDetailLetterError("Select a letter type.");
      return;
    }
    setDetailLetterError("");
    setGeneratingDetailLetter(true);
    try {
      const text = await disputeLettersApi.generateLetter(detailDisputeId, {
        letterType: letterGenLetterType,
        bureauTarget: letterGenBureau,
      });
      setGeneratedLetterBody(text);
    } catch {
      setDetailLetterError("Letter generation failed. Check the API and IntelliWarrior AI configuration.");
    } finally {
      setGeneratingDetailLetter(false);
    }
  };

  const handleSaveDetailLetterSent = async () => {
    if (!id || !detailDisputeId) {
      return;
    }
    if (!generatedLetterBody.trim()) {
      setDetailLetterError("Generate or enter letter text before saving.");
      return;
    }
    const templateName =
      disputeLetterOptions.find((t) => t.typeKey === letterGenLetterType)?.name ?? letterGenLetterType;
    setDetailLetterError("");
    setSavingDetailLetter(true);
    try {
      await disputeLettersApi.saveLetter(detailDisputeId, {
        letterContent: generatedLetterBody,
        letterType: templateName,
        bureauTarget: letterGenBureau,
        markAsSent: true,
      });
      const list = await disputeLettersApi.getLettersForDispute(detailDisputeId);
      setDetailLetters(list);
      await refreshDisputes();
      try {
        const fresh = await disputesApi.getById(id, detailDisputeId);
        setDetailDraft(fresh);
      } catch {
        /* ignore */
      }
    } catch {
      setDetailLetterError("Could not save letter.");
    } finally {
      setSavingDetailLetter(false);
    }
  };

  useEffect(() => {
    const loadClientData = async () => {
      if (!id) {
        setError("Client id is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const [clientData, disputesData, analysisData] = await Promise.all([
          clientsApi.getById(id),
          disputesApi.getAllForClient(id),
          aiApi.getLatestAnalysis(id).catch(() => null),
        ]);

        setClient(clientData);
        setDisputes(disputesData);
        setAnalysis(analysisData);
      } catch {
        setError("Failed to load client detail.");
      } finally {
        setLoading(false);
      }
    };

    void loadClientData();
  }, [id]);

  const loadDocuments = useCallback(async () => {
    if (!id) {
      return;
    }
    setDocsLoading(true);
    setDocsError("");
    try {
      const list = await documentsApi.list(id);
      setDocuments(list);
    } catch {
      setDocsError("Could not load documents.");
    } finally {
      setDocsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === "documents" && id) {
      void loadDocuments();
    }
  }, [activeTab, id, loadDocuments]);

  const loadContracts = useCallback(async () => {
    if (!id) {
      return;
    }
    setContractsLoading(true);
    setContractsError("");
    try {
      const list = await contractsApi.listContractsForClient(id);
      setContracts(list);
    } catch {
      setContractsError("Could not load service agreements.");
    } finally {
      setContractsLoading(false);
    }
  }, [id]);

  const loadSignerAndTemplates = useCallback(async () => {
    try {
      const [s, tpls] = await Promise.all([settingsApi.get(), contractsApi.listTemplates()]);
      setSignerLegalName(s.operatorSignerLegalName ?? "");
      setSignerTitle(s.operatorSignerTitle ?? "");
      setContractTemplates(tpls);
      // Default the send modal to the tenant default template (or the first non-archived one).
      const def = tpls.find((t) => t.isDefault && !t.isArchived) ?? tpls.find((t) => !t.isArchived);
      setSendTemplateId((prev) => prev || def?.id || "");
    } catch {
      // Banner will simply not render if we never read these — silent fail keeps the tab usable.
    }
  }, []);

  useEffect(() => {
    if (activeTab === "documents" && id) {
      void loadContracts();
      void loadSignerAndTemplates();
    }
  }, [activeTab, id, loadContracts, loadSignerAndTemplates]);

  const hasPendingContract = useMemo(
    () =>
      contracts.some(
        (c) => c.status === ContractSignatureStatuses.Sent || c.status === ContractSignatureStatuses.Opened
      ),
    [contracts]
  );

  const signerConfigured =
    (signerLegalName?.trim().length ?? 0) > 0 && (signerTitle?.trim().length ?? 0) > 0;

  // null while we haven't loaded settings yet — keeps the banner from flashing.
  const signerConfigKnown = signerLegalName !== null && signerTitle !== null;

  const sendDisabledReason = !signerConfigKnown
    ? "Loading…"
    : !signerConfigured
      ? "Set operator signer in Settings before sending."
      : hasPendingContract
        ? "A contract is already pending"
        : null;

  const handleOpenSendModal = () => {
    if (sendDisabledReason) return;
    setContractActionError(null);
    if (!sendTemplateId) {
      const def =
        contractTemplates.find((t) => t.isDefault && !t.isArchived) ??
        contractTemplates.find((t) => !t.isArchived);
      setSendTemplateId(def?.id ?? "");
    }
    setSendModalOpen(true);
  };

  const handleConfirmSend = async () => {
    if (!id || !sendTemplateId) return;
    setSending(true);
    setContractActionError(null);
    try {
      await contractsApi.sendContract(id, sendTemplateId);
      setSendModalOpen(false);
      await loadContracts();
    } catch (err) {
      const payload = getApiErrorPayload(err);
      const message = payload?.message ?? "Could not send contract.";
      const is422 =
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err as any).response?.status === 422;
      setContractActionError({ message, isSignerConfig: is422 });
      setSendModalOpen(false);
    } finally {
      setSending(false);
    }
  };

  const handleConfirmVoid = async () => {
    if (!voidTarget) return;
    setVoiding(true);
    setContractActionError(null);
    try {
      await contractsApi.voidContract(voidTarget.id, voidReason.trim() || undefined);
      setVoidTarget(null);
      setVoidReason("");
      await loadContracts();
    } catch (err) {
      const payload = getApiErrorPayload(err);
      setContractActionError({
        message: payload?.message ?? "Could not void contract.",
        isSignerConfig: false,
      });
      setVoidTarget(null);
    } finally {
      setVoiding(false);
    }
  };

  const handleConfirmResend = async () => {
    if (!resendTarget || !id) return;
    setResending(true);
    setContractActionError(null);
    try {
      await contractsApi.sendContract(id, resendTarget.templateId);
      setResendTarget(null);
      await loadContracts();
    } catch (err) {
      const payload = getApiErrorPayload(err);
      const is422 =
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err as any).response?.status === 422;
      setContractActionError({
        message: payload?.message ?? "Could not resend contract.",
        isSignerConfig: is422,
      });
      setResendTarget(null);
    } finally {
      setResending(false);
    }
  };

  const handleArchive = async () => {
    if (!id || archiveSubmitting) return;
    setArchiveSubmitting(true);
    setArchiveError(null);
    try {
      await clientsApi.archive(id);
      setArchiveModalOpen(false);
      navigate("/clients", { replace: true });
    } catch (err) {
      const apiPayload = getApiErrorPayload(err);
      setArchiveError(apiPayload?.message ?? "Could not archive client. Please try again.");
      setArchiveModalOpen(false);
    } finally {
      setArchiveSubmitting(false);
    }
  };

  const handleDownloadContract = (row: ContractSignatureSummaryResponse) => {
    if (!client) return;
    const slug = `${client.firstName}-${client.lastName}`.toLowerCase().replace(/[^a-z0-9-]+/g, "-");
    const fileName = `service-agreement-${slug || "client"}-${row.id.slice(0, 8)}.pdf`;
    void contractsApi.downloadContractPdf(row.id, fileName).catch(() => {
      setContractActionError({ message: "Download failed.", isSignerConfig: false });
    });
  };

  const filteredDocuments = useMemo(() => {
    if (categoryFilter === "All") {
      return documents;
    }
    return documents.filter((d) => d.category === categoryFilter);
  }, [documents, categoryFilter]);

  const refreshClientAndDisputes = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      const [clientData, disputesData] = await Promise.all([
        clientsApi.getById(id),
        disputesApi.getAllForClient(id),
      ]);
      setClient(clientData);
      setDisputes(disputesData);
    } catch {
      /* ignore */
    }
  }, [id]);

  const handleParseCreditReport = async (documentId: string) => {
    setParseLoadingDocId(documentId);
    setDocsError("");
    try {
      const res = await aiApi.parseCreditReport(documentId);
      const parsed = res.parsed;
      setParseView({
        ...parsed,
        tradelines: (parsed.tradelines ?? []).map((t) => ({
          ...t,
          bureaus: t.bureaus ?? [],
          selected: t.selected !== false,
        })),
        inquiries: parsed.inquiries ?? [],
        summary: parsed.summary ?? null,
      });
      setParseModalError("");
      setParseModalOpen(true);
    } catch (err) {
      setDocsError(getAiApiErrorMessage(err, "Could not parse credit report."));
    } finally {
      setParseLoadingDocId(null);
    }
  };

  const handleApplyParsedReport = async () => {
    if (!id || !parseView) {
      return;
    }
    setParseApplying(true);
    setParseModalError("");
    try {
      const result = await aiApi.applyParsedReport({
        clientId: id,
        scores: parseView.scores ?? undefined,
        tradelines: parseView.tradelines?.filter((t) => t.isNegative),
      });
      setParseModalOpen(false);
      setParseView(null);
      void refreshClientAndDisputes();
      void loadDocuments();
      setApplyReportSuccessMessage(result.message);
      setApplyReportSuccessOpen(true);
    } catch (err) {
      setParseModalError(getAiApiErrorMessage(err, "Could not apply parsed report."));
    } finally {
      setParseApplying(false);
    }
  };

  const uploadFiles = async (files: FileList | File[] | null) => {
    if (!id || !files?.length) {
      return;
    }
    const file = files[0];
    const validation = validateDocumentFile(file);
    if (validation) {
      setDocsError(validation);
      return;
    }
    setUploading(true);
    setDocsError("");
    try {
      await documentsApi.upload(id, file, uploadCategory, uploadDescription);
      setUploadDescription("");
      await loadDocuments();
    } catch (e) {
      setDocsError(e instanceof Error ? e.message : "Upload failed. Try a smaller file or check your connection.");
    } finally {
      setUploading(false);
    }
  };

  const confirmDeleteDocument = async () => {
    const target = docDeleteTarget;
    setDocDeleteTarget(null);
    if (!target) {
      return;
    }
    try {
      await documentsApi.remove(target.id);
      await loadDocuments();
    } catch {
      setDocsError("Delete failed.");
    }
  };

  const avgScore = useMemo(() => {
    if (!client) return 0;
    return Math.round((client.equifaxScore + client.experianScore + client.transUnionScore) / 3);
  }, [client]);

  const progress = useMemo(() => {
    if (disputes.length === 0) {
      return 0;
    }
    const resolved = disputes.filter((d) => d.status === "Deleted/Resolved").length;
    const pct = Math.round((resolved / disputes.length) * 100);
    return Math.min(100, Math.max(5, pct));
  }, [disputes]);

  const rankedDisputes = useMemo(
    () => [...disputes].sort((a, b) => b.priorityRank - a.priorityRank).slice(0, 8),
    [disputes]
  );

  const actionSteps = useMemo(() => {
    const fromColumn = parseActionPlanStepsJson(analysis?.actionPlanStepsJson);
    if (fromColumn.length > 0) {
      return fromColumn;
    }
    return parseActionStepsFromFullJson(analysis?.fullAnalysisJson);
  }, [analysis?.actionPlanStepsJson, analysis?.fullAnalysisJson]);

  const analysisHeadline = useMemo(
    () => parseHeadlineFromFullJson(analysis?.fullAnalysisJson, analysis?.actionPlanHeadline ?? ""),
    [analysis?.fullAnalysisJson, analysis?.actionPlanHeadline]
  );

  const scoreImprovementRange = useMemo(() => {
    if (!analysis) {
      return { low: 0, high: 0 };
    }
    return parseScoreRangeFromFullJson(
      analysis.fullAnalysisJson,
      analysis.estimatedScoreImprovementLow,
      analysis.estimatedScoreImprovementHigh
    );
  }, [analysis]);

  const analystNoteContent = useMemo(() => {
    const note = analysis?.analystNote?.trim();
    if (!note) {
      return null;
    }
    try {
      const parsed = JSON.parse(note) as unknown;
      if (Array.isArray(parsed)) {
        return (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-300">
            {parsed.map((item, i) => (
              <li key={i}>{typeof item === "string" ? item : JSON.stringify(item)}</li>
            ))}
          </ul>
        );
      }
    } catch {
      /* plain text */
    }
    return <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">{note}</p>;
  }, [analysis?.analystNote]);

  const runAiAnalysis = async () => {
    if (!client || !id) {
      return;
    }
    setAiRunning(true);
    setAiError("");
    try {
      await aiApi.analyzeCreditReport({
        clientId: id,
        creditReportJson: JSON.stringify({
          generatedAt: new Date().toISOString(),
          source: "ClientDetailPage",
          clientProfile: {
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.email,
            status: client.status,
            scores: {
              equifax: client.equifaxScore,
              experian: client.experianScore,
              transUnion: client.transUnionScore,
            },
          },
          disputes: disputes.map((d) => ({
            creditor: d.creditor,
            status: d.status,
            accountType: d.accountType,
            balance: d.balance,
            disputeReason: d.disputeReason,
            priorityRank: d.priorityRank,
            estimatedScoreImpact: d.estimatedScoreImpact,
            bureaus: {
              equifax: d.equifaxDisputed,
              experian: d.experianDisputed,
              transUnion: d.transUnionDisputed,
            },
          })),
        }),
      });
      const latest = await aiApi.getLatestAnalysis(id);
      setAnalysis(latest);
    } catch {
      setAiError("AI analysis failed. Please try again.");
    } finally {
      setAiRunning(false);
    }
  };

  return (
    <DashboardLayout>
      {loading && (
        <div aria-busy="true" aria-label="Loading client">
          <DetailPageSkeleton />
        </div>
      )}
      {!loading && error && <p className="text-sm text-red-300">{error}</p>}

      {!loading && !error && client && (
        <div className="space-y-6">
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-white">{`${client.firstName} ${client.lastName}`}</h1>
                <p className="mt-1 text-sm text-slate-400">{client.email}</p>
              </div>
              <span className="rounded-full border border-blue-500/40 bg-blue-500/20 px-3 py-1 text-sm text-blue-300">
                {client.status}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <span className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-sm text-slate-200">
                EQ: {client.equifaxScore}
              </span>
              <span className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-sm text-slate-200">
                EX: {client.experianScore}
              </span>
              <span className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-sm text-slate-200">
                TU: {client.transUnionScore}
              </span>
            </div>
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900 p-3">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded-lg px-3 py-2 text-sm transition ${
                    activeTab === tab.key ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </section>

          {activeTab === "overview" && (
            <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 xl:col-span-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Client Information</h3>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/clients/${id}/edit`)}
                      className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-800 hover:text-white"
                    >
                      Edit client
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setArchiveError(null);
                        setArchiveModalOpen(true);
                      }}
                      className="rounded-lg border border-red-900/50 px-3 py-1.5 text-xs font-medium text-red-300 transition hover:bg-red-950/40 hover:text-red-200 hover:border-red-800"
                    >
                      Archive
                    </button>
                  </div>
                </div>
                {archiveError && (
                  <div
                    role="alert"
                    className="mt-3 rounded-lg border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-200"
                  >
                    {archiveError}
                  </div>
                )}
                <div className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                  <p className="text-slate-300">Phone: {client.phone || "-"}</p>
                  <p className="text-slate-300">Address: {client.address || "-"}</p>
                  <p className="text-slate-300">City/State: {`${client.city || "-"}, ${client.state || "-"}`}</p>
                  <p className="text-slate-300">Zip: {client.zipCode || "-"}</p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
                <h3 className="text-lg font-semibold text-white">Progress Meter</h3>
                <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-800">
                  <div className="h-full bg-blue-500" style={{ width: `${progress}%` }} />
                </div>
                <p className="mt-2 text-sm text-slate-400">
                  {disputes.length === 0
                    ? "Add disputes to track resolution progress."
                    : `${progress}% of disputed items resolved`}
                </p>
                <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Score History</p>
                  <p className="mt-1 text-sm text-slate-300">
                    Current average: <span className="font-semibold text-white">{avgScore}</span>
                  </p>
                </div>
              </div>
            </section>
          )}

          {activeTab === "disputes" && (
            <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-white">Disputes</h3>
                <button
                  type="button"
                  onClick={openAddDisputeModal}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
                >
                  Add Dispute
                </button>
              </div>
              {disputes.length === 0 ? (
                <p className="text-sm text-slate-400">No disputes found for this client.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 text-slate-400">
                        <th className="pb-3">Creditor</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Letter Type</th>
                        <th className="pb-3">Bureau</th>
                      </tr>
                    </thead>
                    <tbody>
                      {disputes.map((d) => (
                        <tr
                          key={d.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openDisputeDetail(d)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openDisputeDetail(d);
                            }
                          }}
                          className="cursor-pointer border-b border-slate-800/60 text-slate-200 outline-none hover:bg-slate-800/50 focus-visible:ring-2 focus-visible:ring-blue-500/60"
                        >
                          <td className="py-3">{d.creditor}</td>
                          <td className="py-3">{d.status}</td>
                          <td className="py-3">{d.letterType || "-"}</td>
                          <td className="py-3">
                            {[
                              d.equifaxDisputed ? "Equifax" : "",
                              d.experianDisputed ? "Experian" : "",
                              d.transUnionDisputed ? "TransUnion" : "",
                            ]
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {addDisputeOpen ? (
                <div
                  className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="add-dispute-title"
                >
                  <div className={`${modalCardClass} max-h-[90vh] w-full max-w-lg overflow-y-auto`}>
                    <h3 id="add-dispute-title" className="text-lg font-semibold text-white">
                      Add dispute
                    </h3>
                    <form className="mt-4 space-y-3" onSubmit={(e) => void submitNewDispute(e)}>
                      <label className="block text-xs text-slate-500">
                        Creditor name
                        <input
                          className={modalInputClass}
                          value={dCreditor}
                          onChange={(e) => setDCreditor(e.target.value)}
                          required
                          autoComplete="organization"
                        />
                      </label>
                      <label className="block text-xs text-slate-500">
                        Account type
                        <select
                          className={modalInputClass}
                          value={dAccountType}
                          onChange={(e) => setDAccountType(e.target.value)}
                        >
                          {DISPUTE_ACCOUNT_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-xs text-slate-500">
                        Account number
                        <input
                          className={modalInputClass}
                          value={dAccountNumber}
                          onChange={(e) => setDAccountNumber(e.target.value)}
                          required
                          autoComplete="off"
                        />
                      </label>
                      <label className="block text-xs text-slate-500">
                        Balance
                        <input
                          type="number"
                          step="0.01"
                          className={modalInputClass}
                          value={dBalance}
                          onChange={(e) => setDBalance(e.target.value)}
                          required
                        />
                      </label>
                      <label className="block text-xs text-slate-500">
                        Status
                        <select
                          className={modalInputClass}
                          value={dStatus}
                          onChange={(e) => setDStatus(e.target.value)}
                        >
                          {DISPUTE_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="block">
                        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="text-xs text-slate-500">Dispute reason</span>
                            {(!dCreditor.trim() || !dAccountType) && (
                              <span
                                className="text-[11px] text-slate-500"
                                title="Fill in creditor name and account type first"
                              >
                                Fill in creditor name and account type first
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => void generateDisputeReasonWithAi()}
                            disabled={generatingReason || !dCreditor.trim() || !dAccountType}
                            title={
                              !dCreditor.trim() || !dAccountType
                                ? "Fill in creditor name and account type first"
                                : "Generate a draft dispute reason with IntelliWarrior AI (review before saving)"
                            }
                            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600/20 px-3 py-1.5 text-xs text-emerald-400 transition-colors hover:bg-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {generatingReason ? (
                              <>
                                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" aria-hidden>
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                  />
                                </svg>
                                Generating…
                              </>
                            ) : (
                              <>
                                <svg
                                  className="h-3 w-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 10V3L4 14h7v7l9-11h-7z"
                                  />
                                </svg>
                                Generate with AI
                              </>
                            )}
                          </button>
                        </div>
                        <textarea
                          className={`${modalInputClass} min-h-[100px] resize-y`}
                          rows={4}
                          value={dDisputeReason}
                          onChange={(e) => setDDisputeReason(e.target.value)}
                          placeholder="Describe why this item should be disputed…"
                          required
                        />
                      </div>
                      <fieldset className="rounded-lg border border-slate-800 p-3">
                        <legend className="px-1 text-xs text-slate-500">Bureaus</legend>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-200">
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              checked={dEquifax}
                              onChange={(e) => setDEquifax(e.target.checked)}
                              className="rounded border-slate-600"
                            />
                            Equifax
                          </label>
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              checked={dExperian}
                              onChange={(e) => setDExperian(e.target.checked)}
                              className="rounded border-slate-600"
                            />
                            Experian
                          </label>
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              checked={dTransUnion}
                              onChange={(e) => setDTransUnion(e.target.checked)}
                              className="rounded border-slate-600"
                            />
                            TransUnion
                          </label>
                        </div>
                      </fieldset>
                      <label className="block text-xs text-slate-500">
                        Letter type
                        <select
                          className={modalInputClass}
                          value={dLetterType}
                          onChange={(e) => setDLetterType(e.target.value)}
                          required
                        >
                          {disputeLetterOptions.map((t) => (
                            <option key={t.typeKey} value={t.typeKey}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-xs text-slate-500">
                        Notes
                        <textarea
                          className={`${modalInputClass} min-h-[72px]`}
                          value={dNotes}
                          onChange={(e) => setDNotes(e.target.value)}
                        />
                      </label>
                      {disputeFormError && <p className="text-sm text-rose-300">{disputeFormError}</p>}
                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                          onClick={() => setAddDisputeOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={disputeSaving || !dLetterType}
                          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {disputeSaving ? "Saving…" : "Submit"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              ) : null}

              {detailDisputeId && detailDraft ? (
                <div
                  className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-4 sm:items-center"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="dispute-detail-title"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      closeDetailDisputeModal();
                    }
                  }}
                >
                  <div
                    className={`${modalCardClass} max-h-[90vh] w-full max-w-2xl overflow-y-auto`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 id="dispute-detail-title" className="text-lg font-semibold text-white">
                      Dispute details
                    </h3>
                    <p className="mt-1 text-sm text-slate-400">{detailDraft.creditor}</p>

                    <form className="mt-4 space-y-3" onSubmit={(e) => void submitDetailDisputeSave(e)}>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block text-xs text-slate-500 sm:col-span-2">
                          Creditor name
                          <input
                            className={modalInputClass}
                            value={detailDraft.creditor}
                            onChange={(e) =>
                              setDetailDraft((prev) => (prev ? { ...prev, creditor: e.target.value } : null))
                            }
                            autoComplete="organization"
                          />
                        </label>
                        <label className="block text-xs text-slate-500">
                          Account type
                          <select
                            className={modalInputClass}
                            value={detailDraft.accountType}
                            onChange={(e) =>
                              setDetailDraft((prev) => (prev ? { ...prev, accountType: e.target.value } : null))
                            }
                          >
                            {DISPUTE_ACCOUNT_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-xs text-slate-500">
                          Account number
                          <input
                            className={modalInputClass}
                            value={detailDraft.accountNumber}
                            onChange={(e) =>
                              setDetailDraft((prev) => (prev ? { ...prev, accountNumber: e.target.value } : null))
                            }
                            autoComplete="off"
                          />
                        </label>
                        <label className="block text-xs text-slate-500">
                          Balance
                          <input
                            type="number"
                            step="0.01"
                            className={modalInputClass}
                            value={detailDraft.balance}
                            onChange={(e) =>
                              setDetailDraft((prev) =>
                                prev ? { ...prev, balance: Number.parseFloat(e.target.value) || 0 } : null
                              )
                            }
                          />
                        </label>
                        <label className="block text-xs text-slate-500">
                          Status
                          <select
                            className={modalInputClass}
                            value={detailDraft.status}
                            onChange={(e) =>
                              setDetailDraft((prev) => (prev ? { ...prev, status: e.target.value } : null))
                            }
                          >
                            {DISPUTE_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <label className="block text-xs text-slate-500">
                        Dispute reason
                        <textarea
                          className={`${modalInputClass} min-h-[100px] resize-y`}
                          rows={4}
                          value={detailDraft.disputeReason}
                          onChange={(e) =>
                            setDetailDraft((prev) => (prev ? { ...prev, disputeReason: e.target.value } : null))
                          }
                        />
                      </label>

                      <fieldset className="rounded-lg border border-slate-800 p-3">
                        <legend className="px-1 text-xs text-slate-500">Bureaus</legend>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-200">
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              checked={detailDraft.equifaxDisputed}
                              onChange={(e) =>
                                setDetailDraft((prev) =>
                                  prev ? { ...prev, equifaxDisputed: e.target.checked } : null
                                )
                              }
                              className="rounded border-slate-600"
                            />
                            Equifax
                          </label>
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              checked={detailDraft.experianDisputed}
                              onChange={(e) =>
                                setDetailDraft((prev) =>
                                  prev ? { ...prev, experianDisputed: e.target.checked } : null
                                )
                              }
                              className="rounded border-slate-600"
                            />
                            Experian
                          </label>
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              checked={detailDraft.transUnionDisputed}
                              onChange={(e) =>
                                setDetailDraft((prev) =>
                                  prev ? { ...prev, transUnionDisputed: e.target.checked } : null
                                )
                              }
                              className="rounded border-slate-600"
                            />
                            TransUnion
                          </label>
                        </div>
                      </fieldset>

                      <label className="block text-xs text-slate-500">
                        Letter type
                        <select
                          className={modalInputClass}
                          value={
                            disputeLetterOptions.some((t) => t.typeKey === detailDraft.letterType)
                              ? detailDraft.letterType
                              : disputeLetterOptions[0]?.typeKey ?? ""
                          }
                          onChange={(e) =>
                            setDetailDraft((prev) => (prev ? { ...prev, letterType: e.target.value } : null))
                          }
                        >
                          {disputeLetterOptions.map((t) => (
                            <option key={t.typeKey} value={t.typeKey}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="block text-xs text-slate-500">
                        Notes
                        <textarea
                          className={`${modalInputClass} min-h-[72px]`}
                          value={detailDraft.notes}
                          onChange={(e) =>
                            setDetailDraft((prev) => (prev ? { ...prev, notes: e.target.value } : null))
                          }
                        />
                      </label>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block text-xs text-slate-500">
                          Priority rank
                          <input
                            type="number"
                            step="1"
                            className={modalInputClass}
                            value={detailDraft.priorityRank}
                            onChange={(e) =>
                              setDetailDraft((prev) =>
                                prev ? { ...prev, priorityRank: Number.parseInt(e.target.value, 10) || 0 } : null
                              )
                            }
                          />
                        </label>
                        <label className="block text-xs text-slate-500">
                          Round number
                          <input
                            type="number"
                            step="1"
                            min={1}
                            className={modalInputClass}
                            value={detailDraft.roundNumber}
                            onChange={(e) =>
                              setDetailDraft((prev) =>
                                prev ? { ...prev, roundNumber: Math.max(1, Number.parseInt(e.target.value, 10) || 1) } : null
                              )
                            }
                          />
                        </label>
                        <label className="block text-xs text-slate-500">
                          Last letter sent
                          <input
                            type="datetime-local"
                            className={modalInputClass}
                            value={isoToDatetimeLocalValue(detailDraft.lastLetterSentAt)}
                            onChange={(e) =>
                              setDetailDraft((prev) =>
                                prev
                                  ? { ...prev, lastLetterSentAt: datetimeLocalValueToIso(e.target.value) }
                                  : null
                              )
                            }
                          />
                        </label>
                        <label className="block text-xs text-slate-500">
                          Response received
                          <input
                            type="datetime-local"
                            className={modalInputClass}
                            value={isoToDatetimeLocalValue(detailDraft.responseReceivedAt)}
                            onChange={(e) =>
                              setDetailDraft((prev) =>
                                prev
                                  ? { ...prev, responseReceivedAt: datetimeLocalValueToIso(e.target.value) }
                                  : null
                              )
                            }
                          />
                        </label>
                      </div>

                      {detailSaveError && <p className="text-sm text-rose-300">{detailSaveError}</p>}

                      <div className="flex justify-end gap-2 border-t border-slate-800 pt-3">
                        <button
                          type="button"
                          className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                          onClick={closeDetailDisputeModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={detailSaving}
                          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {detailSaving ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </form>

                    <div className="mt-6 border-t border-slate-800 pt-4">
                      <h4 className="text-sm font-semibold text-white">Generate letter</h4>
                      <p className="mt-1 text-xs text-slate-500">
                        Uses the same AI letter flow as the Dispute letters page.
                      </p>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <label className="block text-xs text-slate-500">
                          Letter type
                          <select
                            className={modalInputClass}
                            value={letterGenLetterType}
                            onChange={(e) => setLetterGenLetterType(e.target.value)}
                          >
                            {disputeLetterOptions.map((t) => (
                              <option key={t.typeKey} value={t.typeKey}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-xs text-slate-500">
                          Bureau target
                          <select
                            className={modalInputClass}
                            value={letterGenBureau}
                            onChange={(e) => setLetterGenBureau(e.target.value)}
                          >
                            {LETTER_BUREAU_TARGETS.map((b) => (
                              <option key={b} value={b}>
                                {b}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleGenerateDetailLetter()}
                        disabled={generatingDetailLetter || !letterGenLetterType}
                        className="mt-3 rounded-lg bg-emerald-600/20 px-4 py-2 text-sm font-medium text-emerald-400 hover:bg-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {generatingDetailLetter ? "Generating…" : "Generate Letter"}
                      </button>
                      <label className="mt-3 block text-xs text-slate-500">
                        Letter content
                        <textarea
                          className={`${modalInputClass} min-h-[160px] resize-y font-mono text-xs`}
                          value={generatedLetterBody}
                          onChange={(e) => setGeneratedLetterBody(e.target.value)}
                          placeholder="Generate a letter or paste text here…"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => void handleSaveDetailLetterSent()}
                        disabled={savingDetailLetter || !generatedLetterBody.trim()}
                        className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {savingDetailLetter ? "Saving…" : "Save & Mark as Sent"}
                      </button>
                      {detailLetterError && (
                        <p className="mt-2 text-sm text-rose-300">{detailLetterError}</p>
                      )}
                    </div>

                    <div className="mt-6 border-t border-slate-800 pt-4">
                      <h4 className="text-sm font-semibold text-white">Letter history</h4>
                      {detailLettersLoading ? (
                        <p className="mt-2 text-sm text-slate-500">Loading letters…</p>
                      ) : detailLetters.length === 0 ? (
                        <p className="mt-2 text-sm text-slate-500">No letters saved for this dispute yet.</p>
                      ) : (
                        <ul className="mt-3 space-y-2">
                          {detailLetters.map((letter) => (
                            <li
                              key={letter.id}
                              className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-300"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                  <span className="font-medium text-slate-200">{letter.letterType}</span>
                                  <span className="mx-2 text-slate-600">·</span>
                                  <span className="text-xs text-slate-500">
                                    {new Date(letter.createdAt).toLocaleString()}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedHistoryLetterId((prev) =>
                                      prev === letter.id ? null : letter.id
                                    )
                                  }
                                  className="rounded-md border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                                >
                                  {expandedHistoryLetterId === letter.id ? "Hide" : "View"}
                                </button>
                              </div>
                              {expandedHistoryLetterId === letter.id ? (
                                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded border border-slate-800 bg-slate-950 p-2 text-xs text-slate-300">
                                  {letter.letterContent}
                                </pre>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
          )}

          {activeTab === "documents" && (
            <ServiceAgreementsSection
              loading={contractsLoading}
              contracts={contracts}
              error={contractsError}
              actionError={contractActionError}
              onDismissActionError={() => setContractActionError(null)}
              signerConfigKnown={signerConfigKnown}
              signerConfigured={signerConfigured}
              sendDisabledReason={sendDisabledReason}
              onSend={handleOpenSendModal}
              onVoid={(row) => {
                setVoidReason("");
                setVoidTarget(row);
              }}
              onResend={(row) => setResendTarget(row)}
              onDownload={handleDownloadContract}
            />
          )}

          {activeTab === "documents" && (
            <section className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">Documents</h3>
                  <p className="mt-1 text-sm text-slate-400">Upload, filter, and download client files.</p>
                </div>
                <div className="flex flex-col gap-3 sm:items-end">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs text-slate-500">Category</label>
                    <select
                      value={uploadCategory}
                      onChange={(e) => setUploadCategory(e.target.value)}
                      className="rounded-lg border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200"
                    >
                      {DOCUMENT_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {DOCUMENT_CATEGORY_LABELS[c]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="w-full min-w-0 text-xs text-slate-500 sm:max-w-md">
                    Notes (optional)
                    <textarea
                      className={`${modalInputClass} mt-1 min-h-[64px]`}
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      placeholder="Short description for this file"
                      maxLength={2000}
                    />
                  </label>
                </div>
              </div>

              <div
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.currentTarget === e.target) {
                    setDragActive(false);
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragActive(false);
                  void uploadFiles(e.dataTransfer.files);
                }}
                className={`rounded-xl border-2 border-dashed px-4 py-10 text-center transition ${
                  dragActive
                    ? "border-blue-500 bg-blue-500/10"
                    : "border-slate-700 bg-slate-950/50 hover:border-slate-600"
                }`}
              >
                <p className="text-sm text-slate-300">
                  Drag and drop a file here, or{" "}
                  <label
                    htmlFor={id ? `client-doc-upload-${id}` : undefined}
                    className="cursor-pointer font-semibold text-blue-400 hover:text-blue-300"
                  >
                    browse
                  </label>
                  <input
                    id={id ? `client-doc-upload-${id}` : undefined}
                    type="file"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => void uploadFiles(e.target.files)}
                  />
                </p>
                {uploading && <p className="mt-2 text-xs text-slate-500">Uploading…</p>}
              </div>

              {docsError && <p className="text-sm text-rose-300">{docsError}</p>}

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Filter</span>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200"
                >
                  <option value="All">All categories</option>
                  {DOCUMENT_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {DOCUMENT_CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </div>

              {docsLoading ? (
                <p className="text-sm text-slate-400">Loading documents…</p>
              ) : filteredDocuments.length === 0 ? (
                <p className="text-sm text-slate-400">
                  {documents.length === 0
                    ? "No documents yet. Drag a file here or use browse to upload."
                    : "No documents match this filter."}
                </p>
              ) : (
                <ul className="space-y-2">
                  {filteredDocuments.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-3"
                    >
                      <FileTypeIcon fileName={doc.fileName} fileType={doc.contentType} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-white">{doc.fileName}</p>
                        <p className="text-xs text-slate-500">
                          {documentCategoryLabel(doc.category)} · {formatFileSize(doc.fileSize)} ·{" "}
                          {new Date(doc.createdAt).toLocaleString()}
                          {doc.uploadedBy ? ` · ${doc.uploadedBy}` : ""}
                        </p>
                        {doc.description ? (
                          <p className="mt-1 text-xs text-slate-400">{doc.description}</p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {isPdfDocument(doc) ? (
                          <button
                            type="button"
                            onClick={() => void handleParseCreditReport(doc.id)}
                            disabled={parseLoadingDocId !== null}
                            className="flex items-center gap-1.5 rounded-lg bg-emerald-600/20 px-3 py-1.5 text-xs font-medium text-emerald-400 transition-colors hover:bg-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {parseLoadingDocId === doc.id ? (
                              <>
                                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" aria-hidden>
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                  />
                                </svg>
                                Parsing…
                              </>
                            ) : (
                              <>
                                <svg
                                  className="h-3 w-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                  />
                                </svg>
                                Parse with AI
                              </>
                            )}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() =>
                            void documentsApi.download(doc.id, doc.fileName).catch(() => {
                              setDocsError("Download failed.");
                            })
                          }
                          className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700"
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          onClick={() => setDocDeleteTarget({ id: doc.id, fileName: doc.fileName })}
                          className="rounded-lg border border-rose-500/40 bg-rose-950/30 px-3 py-1.5 text-xs font-medium text-rose-200 hover:bg-rose-950/50"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {activeTab === "ai" && (
            <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-white">AI Analysis</h3>
                <button
                  type="button"
                  onClick={() => void runAiAnalysis()}
                  disabled={aiRunning}
                  className="inline-flex items-center gap-2 rounded-lg border border-purple-400/40 bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {aiRunning && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                  {aiRunning ? "IntelliWarrior AI Analyzing..." : "Run AI Analysis"}
                </button>
              </div>

              {aiError && <p className="mt-3 text-sm text-rose-300">{aiError}</p>}

              {!analysis ? (
                <p className="mt-4 text-sm text-slate-400">
                  No AI analysis found yet for this client. Run analysis to generate a full plan.
                </p>
              ) : (
                <div className="mt-4 space-y-4">
                  {analysisHeadline ? (
                    <h4 className="text-lg font-bold text-white">{analysisHeadline}</h4>
                  ) : null}

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Priority level</p>
                      <span
                        className={`mt-2 inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${
                          analysis.priorityLevel.toLowerCase() === "high"
                            ? "border-rose-500/40 bg-rose-500/20 text-rose-300"
                            : analysis.priorityLevel.toLowerCase() === "medium"
                              ? "border-amber-500/40 bg-amber-500/20 text-amber-300"
                              : "border-emerald-500/40 bg-emerald-500/20 text-emerald-300"
                        }`}
                      >
                        {analysis.priorityLevel}
                      </span>
                    </div>
                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 md:col-span-2">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Score improvement estimate</p>
                      <div className="mt-3 flex flex-wrap gap-6">
                        <div>
                          <p className="text-xs font-medium text-slate-500">Target Score Low</p>
                          <p className="mt-1 text-lg font-semibold text-purple-200">
                            +{scoreImprovementRange.low} pts
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-500">Target Score High</p>
                          <p className="mt-1 text-lg font-semibold text-purple-200">
                            +{scoreImprovementRange.high} pts
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {analystNoteContent ? (
                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                      <p className="text-sm text-slate-400">Analyst note</p>
                      {analystNoteContent}
                    </div>
                  ) : null}

                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="mb-2 text-sm text-slate-400">Dispute plan ranked by priority</p>
                    {rankedDisputes.length === 0 ? (
                      <p className="text-sm text-slate-500">No disputes found to rank.</p>
                    ) : (
                      <ul className="space-y-2 text-sm text-slate-200">
                        {rankedDisputes.map((d) => (
                          <li key={d.id} className="rounded-md border border-slate-800 px-3 py-2">
                            <span className="font-medium text-white">{d.creditor}</span> - priority #{d.priorityRank} -{" "}
                            {d.status}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="mb-2 text-sm text-slate-400">Client action plan steps</p>
                    {actionSteps.length === 0 ? (
                      <p className="text-sm text-slate-500">No action steps returned.</p>
                    ) : (
                      <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-200 marker:text-slate-500">
                        {actionSteps.map((step, idx) => (
                          <li key={`${idx}-${step.slice(0, 40)}`}>{step}</li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {activeTab === "billing" && client && id && (
            <ClientBillingTab clientId={id} clientName={`${client.firstName} ${client.lastName}`} />
          )}

          {activeTab === "notes" && (
            <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
              <h3 className="text-lg font-semibold text-white">Notes</h3>
              <p className="mt-2 text-sm text-slate-400">Advisor notes panel can be connected here.</p>
            </section>
          )}
        </div>
      )}

      <AlertModal
        isOpen={applyReportSuccessOpen}
        title="Credit report applied"
        message={applyReportSuccessMessage}
        variant="success"
        buttonLabel="OK"
        onClose={() => {
          setApplyReportSuccessOpen(false);
          setApplyReportSuccessMessage("");
        }}
      />

      <CreditReportParseModal
        open={parseModalOpen}
        onClose={() => {
          setParseModalOpen(false);
          setParseView(null);
          setParseModalError("");
        }}
        parseResult={parseView}
        onChangeTradelines={(next) =>
          setParseView((prev) => (prev ? { ...prev, tradelines: next } : prev))
        }
        onApply={() => void handleApplyParsedReport()}
        applying={parseApplying}
        error={parseModalError}
      />

      <ConfirmModal
        isOpen={archiveModalOpen}
        title={`Archive ${client?.firstName ?? ""} ${client?.lastName ?? ""}?`}
        message="This client will be hidden from your active clients list, dashboards, automations, and billing. Existing disputes, documents, and signed contracts are preserved. You can restore the client from the archived clients page at any time."
        confirmLabel={archiveSubmitting ? "Archiving…" : "Archive"}
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={() => void handleArchive()}
        onCancel={() => {
          if (archiveSubmitting) return;
          setArchiveModalOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={docDeleteTarget !== null}
        title="Delete Document"
        message={
          docDeleteTarget && client
            ? `Permanently delete "${docDeleteTarget.fileName}" from ${client.firstName} ${client.lastName}'s documents? This cannot be undone.`
            : docDeleteTarget
              ? `Permanently delete "${docDeleteTarget.fileName}"? This cannot be undone.`
              : ""
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={() => void confirmDeleteDocument()}
        onCancel={() => setDocDeleteTarget(null)}
      />

      {sendModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm iw-modal-backdrop-in"
          role="presentation"
          onClick={() => !sending && setSendModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="send-contract-title"
            className="iw-modal-panel-in w-full max-w-md rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="send-contract-title" className="text-lg font-semibold text-white">
              Send for signature
            </h2>
            <p className="mt-2 text-sm text-slate-300">
              An invitation will be emailed to{" "}
              <span className="font-medium text-white">{client?.email ?? "the client"}</span> with
              a secure signing link valid for 14 days.
            </p>

            <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Template
            </label>
            <select
              value={sendTemplateId}
              onChange={(e) => setSendTemplateId(e.target.value)}
              disabled={sending}
              className={`${modalInputClass} mt-1`}
            >
              {contractTemplates
                .filter((t) => !t.isArchived)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.isDefault ? " (default)" : ""} · ${(t.monthlyFeeCents / 100).toFixed(2)}/mo
                  </option>
                ))}
            </select>
            {contractTemplates.filter((t) => !t.isArchived).length === 0 && (
              <p className="mt-2 text-xs text-amber-300">
                No active templates. Visit{" "}
                <Link to="/contracts/templates" className="underline hover:text-amber-200">
                  Contract Templates
                </Link>{" "}
                to set one up.
              </p>
            )}

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={sending}
                onClick={() => setSendModalOpen(false)}
                className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={sending || !sendTemplateId}
                onClick={() => void handleConfirmSend()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={voidTarget !== null}
        title="Void contract"
        message={
          voidTarget
            ? `Void the ${voidTarget.templateName} contract sent ${new Date(voidTarget.sentAt).toLocaleDateString()}? The signing link will stop working immediately.`
            : ""
        }
        confirmLabel={voiding ? "Voiding…" : "Void"}
        confirmVariant="danger"
        onConfirm={() => void handleConfirmVoid()}
        onCancel={() => {
          if (voiding) return;
          setVoidTarget(null);
          setVoidReason("");
        }}
      />

      <ConfirmModal
        isOpen={resendTarget !== null}
        title="Resend contract"
        message={
          resendTarget
            ? `Send a new invitation using the ${resendTarget.templateName} template? The previous link is already inactive.`
            : ""
        }
        confirmLabel={resending ? "Sending…" : "Resend"}
        confirmVariant="primary"
        onConfirm={() => void handleConfirmResend()}
        onCancel={() => {
          if (resending) return;
          setResendTarget(null);
        }}
      />
    </DashboardLayout>
  );
};

export default ClientDetailPage;
